import { ColumnDef, flexRender, Row, Table } from '@tanstack/react-table';
import { ActiveSourcingChannelDetails } from 'api-clients/monolith';
import clsx from 'clsx';
import _range from 'lodash/range';
import _startCase from 'lodash/startCase';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { PropsWithChildren, useState } from 'react';
import { useIntl } from 'react-intl';

import EmptyCampaigns from '@/assets/icons/empty-campaigns.svg';
import UserCheck from '@/assets/icons/user-check.svg';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Tag } from '@/components/ui/tag';
import { REACH } from '@/types/channels';

import { BudgetRecommendation } from './budget-recommendation';
import { CampaignDataUpdateBanner } from './campaign-data-update-banner';
import { ChannelCannotBePausedBanner } from './channel-cannot-be-paused-banner';
import { ReachChannels } from './reach-channels';

interface CardsProps {
  table: Table<ActiveSourcingChannelDetails>;
  columns: ColumnDef<ActiveSourcingChannelDetails>[];
}

export function Cards({ table, columns }: CardsProps) {
  const gridTemplateColumns = `repeat(${
    columns.length - 1
  }, minmax(0, 1fr)) 120px`;

  if (table.getRowModel().rows?.length <= 0) return <CardsEmpty />;

  return (
    <>
      {table.getRowModel().rows.map(row => (
        <CardContent
          key={row.id}
          row={row}
          gridTemplateColumns={gridTemplateColumns}
        />
      ))}
    </>
  );
}

function CardContent({
  row,
  gridTemplateColumns,
}: {
  row: Row<ActiveSourcingChannelDetails>;
  gridTemplateColumns: string;
}) {
  const intl = useIntl();
  const [isReachExpanded, setIsReachExpanded] = useState(false);

  const channelSlug = row.original.sourcing_channel.slug;

  const handleExpandReachClick = () => {
    setIsReachExpanded(!isReachExpanded);
  };

  return (
    <div className={clsx('relative', row.original.managed_by && 'mt-3')}>
      {row.original.managed_by && (
        <Tag
          icon={UserCheck}
          text={_startCase(row.original.managed_by)}
          className="bg-fx-teal-50 absolute -top-3 left-3"
        />
      )}
      <RowCard>
        <div className="grid items-start" style={{ gridTemplateColumns }}>
          {row.getVisibleCells().map(cell => (
            <div
              key={cell.id}
              className={clsx(
                'flex flex-col items-start gap-2 py-6 px-5',
                cell.column.id === 'edit' && 'place-self-center',
              )}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}

              {/* Show button to expand reach channels only when the channel is Reach and column is the channel name */}
              {cell.column.id === 'sourcing_channel' &&
                channelSlug === REACH && (
                  <Button
                    variant="link"
                    size="sm"
                    className="ml-8 hover:no-underline"
                    onClick={handleExpandReachClick}
                  >
                    {intl.formatMessage({
                      defaultMessage: 'Channels',
                      description:
                        'Link on Reach card to expand nested publisher sub-rows',
                    })}
                    {isReachExpanded ? <ChevronUp /> : <ChevronDown />}
                  </Button>
                )}
            </div>
          ))}
        </div>
        {/* Reach publishers when expanded */}
        {isReachExpanded &&
          row.original.sourcing_purchase_id &&
          channelSlug === REACH && (
            <ReachChannels
              sourcingPurchaseId={row.original.sourcing_purchase_id}
              gridTemplateColumns={gridTemplateColumns}
            />
          )}

        {/* Increase/decrease budget recommendation */}
        <BudgetRecommendation channel={row.original} />

        {/* Channel cannot be paused banner for Boost and ZipRecruiter */}
        <ChannelCannotBePausedBanner
          channelSlug={channelSlug}
          status={row.original.status}
        />
        {/* Add end date banner */}
        <CampaignDataUpdateBanner campaignData={row.original} />
      </RowCard>
    </div>
  );
}

function RowCard({ children }: PropsWithChildren) {
  return (
    <div
      className={`hover:bg-fx-neutral-50 border-fx-neutral-200 min-h-30 flex flex-col justify-center rounded-lg border bg-white shadow-sm
    transition-colors`}
    >
      {children}
    </div>
  );
}

function CardsEmpty() {
  const intl = useIntl();

  return (
    <div className="border-fx-neutral-200 rounded-lg border bg-white pb-6 text-center">
      <EmptyState
        icon={EmptyCampaigns}
        header={intl.formatMessage({
          defaultMessage: 'No campaigns to show',
          description:
            'Empty state when the opening has no paid channel campaigns',
        })}
        description={intl.formatMessage({
          defaultMessage:
            'There are no campaigns for this opening to show at the moment',
          description:
            'Reassure user: table is empty because nothing is set up yet',
        })}
      />
    </div>
  );
}

Cards.Skeleton = function CardsSkeleton() {
  const gridTemplateColumns = `repeat(${7}, minmax(0, 1fr)) 120px`;

  return (
    <>
      {_range(2).map(rowIndex => (
        <RowCard key={rowIndex}>
          <div className="grid items-center" style={{ gridTemplateColumns }}>
            <div className="flex h-28 flex-col items-start gap-2 py-6 px-5">
              <div className="flex items-center gap-2">
                <Skeleton className="h-6 w-6" />
                <Skeleton className="h-5 w-24" />
              </div>
            </div>
            {_range(6).map(colIndex => (
              <div
                key={colIndex}
                className="flex h-28 flex-col items-start gap-3 py-6 px-5"
              >
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-5 w-24" />
                <Skeleton className="h-1.5 w-full" />
              </div>
            ))}
            <div className="h-30 flex flex-col items-center justify-between gap-2 py-6 px-5">
              <Skeleton className="h-4 w-8" />
              <Skeleton className="h-6 w-6" />
            </div>
          </div>
        </RowCard>
      ))}
    </>
  );
};
